import type { News } from '@maru44/huntre-utils/src/models/news'
import { Box, Card, CardActionArea, CardContent, CircularProgress, Grid, Pagination, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { useNews } from 'src/hooks/news/useNews'
import { formatTimestampToDate } from 'src/utils/firebase'

export const NewsPage = () => {
  const navigate = useNavigate()
  const { listNews } = useNews()

  const totalPages = Math.ceil(listNews.totalCount / listNews.pageSize)

  const handlePageChange = (_: unknown, newPage: number) => {
    listNews.goToPage(newPage - 1) // MUI Pagination is 1-indexed
  }

  if (listNews.error) {
    console.error(listNews.error)
    return null
  }

  if (listNews.isLoading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Breads breadIds={['news']} />
      <Stack spacing={2}>
        <Typography
          variant="h5"
          sx={{
            mt: 0,
          }}
        >
          ニュース
        </Typography>
        {listNews.data && listNews.data.length > 0 ? (
          <>
            <Grid
              container
              sx={{
                rowGap: 1,
              }}
            >
              {listNews.data.map((news) => (
                <Grid key={news.id} size={12}>
                  <NewsCard news={news} onNavigate={() => navigate(`/news/${news.id}`)} />
                </Grid>
              ))}
            </Grid>
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                }}
              >
                <Pagination count={totalPages} page={listNews.page + 1} onChange={handlePageChange} color="primary" />
              </Box>
            )}
          </>
        ) : (
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            ニュースはありません
          </Typography>
        )}
      </Stack>
    </>
  )
}

type NewsCardProps = {
  news: News
  onNavigate: () => void
}

const NewsCard = ({ news, onNavigate }: NewsCardProps) => {
  return (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 8,
        },
      }}
    >
      <CardActionArea onClick={onNavigate} sx={{ flexGrow: 1 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {news.title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {formatTimestampToDate(news.createdAt)}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {news.content}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
